import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { getAirUpsell } from '../../../api/endpoints/air';
import type { OriginDestinationOption, SelectableFare } from '../../../api/types/air';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';

/** Tutarı tr-TR biçiminde ("1.936,74 TL") verir — `AirResultsList`'teki `formatMoney`'nin
 *  hafif bir kopyası (o dosyada export edilmiyor, panel ondan bağımsız). */
function formatMoney(amount?: number | null, currency?: string | null): string {
  if (amount == null) return 'Fiyat yok';
  const n = amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cur = currency === 'TRY' ? 'TL' : currency;
  return cur ? `${n} ${cur}` : n;
}

function fareLabel(fare: SelectableFare): string {
  return fare.fareName ?? fare.fareCode ?? 'Ücret';
}

/** Bir upsell `OriginDestinationOption`'ından `fareCode`'suz sentetik `SelectableFare`
 *  üretir — fiyat `pricingInfo`/`totalFare`'dan, kabin/sınıf/kod ilk segmentten.
 *  Fiyat yoksa `null`. (AirResultsList'teki `synthesizeFareFromPricing`'in eşi;
 *  o dosyada export edilmiyor, panel ondan bağımsız.) */
function synthUpsellFare(option: OriginDestinationOption): SelectableFare | null {
  const pricing = option.pricingInfo;
  const totalFare = pricing?.totalFare ?? option.totalFare ?? null;
  if (totalFare == null) return null;
  const seg = option.flightSegments?.[0];
  return {
    fareCode: seg?.fareCode ?? option.fareCode,
    fareName: option.fareName ?? seg?.cabinType ?? 'Ücret',
    totalFare,
    totalFareTRY: pricing?.totalFareTRY,
    currency: pricing?.currency ?? undefined,
    cabin: seg?.cabinType,
    bookingClass: seg?.bookingClass,
  };
}

/** `pricingInfo.fareBreakdowns[0].amenities` — `PricingBase` bunu modellemez, gevşek
 *  okunur. `isChargeable === false` olanlar dahil imkânlar (legacy görünümü gibi). */
function includedAmenities(option: OriginDestinationOption): string[] {
  const breakdowns = (option.pricingInfo as { fareBreakdowns?: unknown } | undefined)?.fareBreakdowns;
  const first = Array.isArray(breakdowns) ? (breakdowns[0] as { amenities?: unknown } | undefined) : undefined;
  const amenities = Array.isArray(first?.amenities) ? (first?.amenities as { description?: string; isChargeable?: boolean }[]) : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const a of amenities) {
    if (a.isChargeable === false && a.description && !seen.has(a.description)) {
      seen.add(a.description);
      out.push(a.description);
    }
  }
  return out;
}

function farePrice(fare: SelectableFare): string {
  const cur = fare.currency ?? 'TRY';
  if (cur !== 'TRY' && fare.totalFareTRY) return formatMoney(fare.totalFareTRY, 'TRY');
  return formatMoney(fare.totalFare, cur);
}

/**
 * Branded-fare Upsell (`Air/Upsell`) — Allocate adımından hemen önce, kullanıcı bir uçuş +
 * ücret seçtikten sonra gösterilen ara panel. Seçilen opsiyonun `productId`'si + `fareCode`
 * ile `Air/Upsell` çağrılır; dönen alternatif (genelde daha kapsamlı) markalı ücretler
 * listelenir. Kullanıcı:
 *   - "Seçili ücretle devam et" → orijinal seçimle `Air/Allocate`,
 *   - bir alternatif ücret seçerse → o ücretin YENİ `productId`'si ile `Air/Allocate`.
 *
 * `EmployeeID` varsa kurumsal `PolicyFilterUpsell` sunucuda uygulanır — panel yalnız
 * sonucu gösterir. Bu ortamda canlı uçuş envanteri yok: istek/cevap şekli dokümandan,
 * canlı doğrulanmadı (bkz. `flowConfig.ts` başlığı).
 */
export function AirUpsellPanel({
  option,
  fare,
  loading,
  onBack,
  onContinue,
  onSelectUpsell,
}: {
  option: OriginDestinationOption;
  fare: SelectableFare;
  loading: boolean;
  /** Sonuç listesine geri dön (farklı bir uçuş / ücret seç). */
  onBack: () => void;
  /** Seçili ücretle (upsell'siz) `Air/Allocate`'e devam. */
  onContinue: () => void;
  /** Bir alternatif markalı ücret seçildi — yeni option + fare ile `Air/Allocate`'e devam. */
  onSelectUpsell: (option: OriginDestinationOption, fare: SelectableFare) => void;
}) {
  const productId = option.id ?? option.flightSegments?.[0]?.productId;

  const upsellMutation = useMutation({
    mutationFn: () => {
      if (!productId) return Promise.reject(new Error('Seçilen uçuşun ürün kimliği bulunamadı.'));
      // `fareCode` yoksa (yalnız `PricingInfo`'lu GDS itinerary'si) yalnız `productId` ile —
      // legacy `/Flight/Upsell` de `option: id` dışında bir şey göndermez.
      return getAirUpsell({
        allocateItems: [fare.fareCode ? { productId, fareCode: fare.fareCode } : { productId }],
        createShoppingCart: false,
      });
    },
  });

  const alternatives = useMemo(() => {
    const options = upsellMutation.data?.originDestinationOptions ?? [];
    // `Air/Upsell` markalı ücretleri `selectableFares` içinde DEĞİL, her biri kendi
    // `pricingInfo`'suna sahip ayrı `OriginDestinationOption` olarak döndürür (canlı
    // Amadeus cevabı, 2026-09-08) — legacy `international_upsell.js` de option'ı doğrudan
    // `PricingInfo.TotalFare` ile çizer. Her option'dan `fareCode`'suz sentetik bir
    // `SelectableFare` türet (seçilince `opt.id` yeni `productId` olur).
    const seen = new Set<string>();
    const out: { opt: OriginDestinationOption; fare: SelectableFare }[] = [];
    for (const opt of options) {
      const fare = synthUpsellFare(opt);
      if (!fare) continue;
      // Zaten seçili olan itinerary'yi tekrar gösterme.
      if (opt.id && opt.id === option.id) continue;
      // Sağlayıcı aynı `Id`'li option'ı birden çok kez dönebiliyor (fare-family tekrarı) —
      // çift React key'i / kart tekrarını önlemek için tekilleştir.
      const dedupeKey = `${opt.id ?? ''}|${fare.fareCode ?? ''}|${fare.totalFare ?? ''}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      out.push({ opt, fare });
    }
    return out;
  }, [upsellMutation.data, option.id]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2">Seçilen uçuş / ücret</Typography>
            <Typography variant="body2" color="text.secondary">
              {(option.flightSegments ?? [])
                .map((s) => `${s.departureAirportCode ?? '?'}→${s.arrivalAirportCode ?? '?'}`)
                .join('  ·  ') || 'Uçuş segment bilgisi yok'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Chip size="small" color="success" label={fareLabel(fare)} />
              <Typography variant="subtitle1">{farePrice(fare)}</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Box>
        <Button
          variant="outlined"
          disabled={loading || upsellMutation.isPending || !productId}
          onClick={() => upsellMutation.mutate()}
        >
          {upsellMutation.isPending ? 'Yükleniyor…' : 'Diğer Fiyatlar / Paketler'}
        </Button>
      </Box>

      {upsellMutation.isPending && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      <ApiErrorAlert error={upsellMutation.error} />

      {upsellMutation.isSuccess && alternatives.length === 0 && (
        <Alert severity="info">Bu ücret için alternatif markalı ücret sunulmuyor.</Alert>
      )}

      {alternatives.map(({ opt, fare: alt }, i) => {
        const perks = includedAmenities(opt);
        return (
          <Card key={`${opt.id ?? 'opt'}-${i}`} variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ alignItems: { sm: 'flex-start' }, justifyContent: 'space-between' }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {fareLabel(alt)}
                  </Typography>
                  {alt.cabin && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {alt.cabin}
                      {alt.bookingClass ? ` · ${alt.bookingClass}` : ''}
                    </Typography>
                  )}
                  {perks.length > 0 && (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.5, gap: 0.5 }}>
                      {perks.map((perk, k) => (
                        <Chip key={k} size="small" variant="outlined" label={perk} />
                      ))}
                    </Stack>
                  )}
                </Box>
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexShrink: 0 }}>
                  <Typography variant="subtitle1">{farePrice(alt)}</Typography>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={loading}
                    onClick={() => onSelectUpsell(opt, alt)}
                  >
                    Bu ücreti seç
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        );
      })}

      <Stack direction="row" spacing={1}>
        <Button variant="text" disabled={loading} onClick={onBack}>
          Geri
        </Button>
        <Button variant="contained" disabled={loading} onClick={onContinue}>
          {loading ? 'İşleniyor…' : 'Seçili ücretle devam et'}
        </Button>
      </Stack>
    </Box>
  );
}
